import React, {
  useContext, useEffect, useState,
} from 'react';

import classNames from 'classnames';
import { useWindowStore, useAppStore, useMapStateStore } from '../../stores';
import SiteSeeContext from './SiteSeeContext';
import { getActiveGuideData, assembleStepStyles } from './SiteSeeGuideHelpers';
import siteSeeGuideData from './SiteSeeGuideData';

import Modal from '../Modal/Modal';
import ModalContentBasic from '../Modal/ModalContentBasic';

import './SiteSeeGuides.scss';

// There are 2 "modes" of the guides:
// - tour, a full guided tour with steps
// - tip, a single help guide attached to an element.
const SiteSeeGuides = () => {
  const [activeNonTipContent, setActiveNonTipContent] = useState({});
  const [modalStyle, setModalStyle] = useState({});
  const [arrowStyle, setArrowStyle] = useState({});
  const [activeGuideData, setActiveGuideData] = useState({});
  const [styling, setStyling] = useState({});
  const [index, setIndex] = useState(0);
  const [trigger, setTrigger] = useState(null);
  const [onHoldSiteSeeGuideId, setOnHoldSiteSeeGuideId] = useState(null);
  const [activeModalContent, setActiveModalContent] = useState(null);

  // Zustand stores
  const windowWidth = useWindowStore((state) => state.windowWidth);
  const windowHeight = useWindowStore((state) => state.windowHeight);
  const isMobile = useWindowStore((state) => state.isMobile);
  const settings = useAppStore((state) => state.settings);
  const setResetMap = useMapStateStore((state) => state.setResetMap);

  const {
    siteSeeGuideId,
    showSiteSeeGuide,
    setShowSiteSeeGuide,
    siteSeeGuideMode,
    siteSeeModalDims,
    setSiteSeeGuideMode,
    setSiteSeeGuideId,
  } = useContext(SiteSeeContext);

  const hasActiveNonTipContent = Object.keys(activeNonTipContent).length > 0;
  const count = isMobile ? 9 : 7;
  const tipCountPhrase = `Tip ${index}/${count}`;
  const isCoached = siteSeeGuideMode === 'tour';
  const { isProduction } = settings;

  // These activeModalContents happen after the transition is complete.
  const onExitFinishFunc = () => {
    const warning = getActiveGuideData('warning', rawSiteSeeGuideData) || {};
    switch (trigger) {
      case 'introCloseBtn':
        setSiteSeeGuideMode('tip');
        break;
      case 'warningCloseBtn':
        setActiveNonTipContent({});
        setSiteSeeGuideId(onHoldSiteSeeGuideId);
        setOnHoldSiteSeeGuideId(null);
        setShowSiteSeeGuide(true);
        break;
      case 'tourTipCloseBtn':
        setOnHoldSiteSeeGuideId(siteSeeGuideId);
        setSiteSeeGuideId(null);
        setActiveNonTipContent(warning);
        setShowSiteSeeGuide(true);
        break;
      case 'nonTourTipCloseBtn':
        setSiteSeeGuideId(null);
        break;
      case 'startTour':
        setActiveNonTipContent({});
        setSiteSeeGuideId(1);
        setShowSiteSeeGuide(true);
        break;
      case 'endTour':
        setSiteSeeGuideMode('tip');
        setSiteSeeGuideId(null);
        setActiveNonTipContent({});
        setResetMap(true); // Reset map to starting zoom/center
        window?.fathom?.trackEvent('dismiss tour');
        break;
      case 'cancelEndTour':
        setActiveNonTipContent({});
        setSiteSeeGuideId(onHoldSiteSeeGuideId);
        setOnHoldSiteSeeGuideId(null);
        setShowSiteSeeGuide(true);
        break;
      case 'nextTip':
        setSiteSeeGuideId(siteSeeGuideId + 1);
        setShowSiteSeeGuide(true);
        break;
      default:
    }
    setTrigger(null);
  };

  const onNextTipClick = () => {
    window?.fathom?.trackEvent(`tip ${siteSeeGuideId} view`);
    setTrigger('nextTip');
    setShowSiteSeeGuide(false);
  };

  const handleModalHide = () => {
    setShowSiteSeeGuide(false);
    closeBtnFunc();
  };

  // These activeModalContents happen immediately on close btn click and trigger the transition.
  // onExitFinishFunc is called after the transition is done.
  const closeBtnFunc = () => {
    // If a trigger has already been set, its not a close button.
    if (trigger) {
      return;
    }

    // Determine what the close btn is closing.
    let closeBtn = null;
    if (hasActiveNonTipContent) {
      closeBtn = `${activeNonTipContent.id}CloseBtn`;
    } else if (siteSeeGuideMode) {
      const prefix = siteSeeGuideMode === 'tour' ? 'tour' : 'nonTour';
      closeBtn = `${prefix}TipCloseBtn`;
    }

    setTrigger(closeBtn);
  };

  const rawSiteSeeGuideData = siteSeeGuideData(
    setTrigger,
    onNextTipClick,
    handleModalHide,
  );

  const mapShell = document?.querySelector('.mapShell') ? document.querySelector('.mapShell') : null;
  const mapShellClassList = mapShell ? mapShell.classList : null;
  useEffect(() => {
    if (!mapShell) return;

    if (isCoached) {
      mapShellClassList.add('mapShell--tour');
    } else {
      mapShellClassList.remove('mapShell--tour');
    }
  }, [isCoached, mapShell]);

  // When the user returns, don't show the tour for 7 days.
  const setUserSiteSeeTourVisibilityCookie = () => {
    const EXPIRATION_TIME = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds.

    const now = new Date().getTime();
    const userSiteSeeTourVisibilityCookie = {
      value: 'true',
      expiration: now + EXPIRATION_TIME,
    };

    localStorage.setItem('userSiteSeeTourVisibility', JSON.stringify(userSiteSeeTourVisibilityCookie));
  };

  // Check for localStorage of whether user has should be shown the tour.
  useEffect(() => {
    const siteSeeTour = JSON.parse(localStorage.getItem('userSiteSeeTourVisibility'));

    // Returning user.
    if (siteSeeTour !== null && isProduction) {
      const now = new Date().getTime();
      if (now < siteSeeTour.expiration) {
        // Cookie is still valid, reset cookie and hide tour.
        setUserSiteSeeTourVisibilityCookie();
        setSiteSeeGuideMode('tip');
      }
    } else {
      setShowSiteSeeGuide(true);
      setSiteSeeGuideMode('tour');
      if (isProduction) {
        setUserSiteSeeTourVisibilityCookie();
      }
    }
  }, [isProduction]);

  // On guide mode change to tour, intro is the active modal content.
  useEffect(() => {
    if (siteSeeGuideMode === 'tour') {
      setShowSiteSeeGuide(true);
      const rawActiveGuideData = getActiveGuideData('intro', rawSiteSeeGuideData) || {};
      setActiveNonTipContent(rawActiveGuideData);
    } else {
      setSiteSeeGuideId(null);
    }
  }, [siteSeeGuideMode]);

  // Set what content is being shown in the modal.
  const rawActiveGuideData = getActiveGuideData(siteSeeGuideId, rawSiteSeeGuideData, isMobile)
    || {};
  useEffect(() => {
    if (siteSeeGuideMode === 'tour' && hasActiveNonTipContent) {
      setStyling({});
      setActiveModalContent(activeNonTipContent);
    } else if (siteSeeGuideId) {
      setActiveGuideData(rawActiveGuideData);
      setIndex(isMobile ? rawActiveGuideData.mobileIndex : rawActiveGuideData.desktopIndex);
      setStyling(rawActiveGuideData.styling || {});
      setActiveModalContent(rawActiveGuideData);
    }
  }, [windowWidth,
    isMobile,
    siteSeeGuideMode,
    hasActiveNonTipContent,
    siteSeeGuideId]);

  // Get position for anchored modals.
  const currentSiteSeeModalDims = siteSeeModalDims?.find(
    (item) => item.id === activeGuideData.id,
  );

  useEffect(
    () => {
      const { rawModalStyle, rawArrowStyle } = assembleStepStyles(
        currentSiteSeeModalDims,
        isMobile,
        styling,
        windowWidth,
        windowHeight,
      );
      setModalStyle(rawModalStyle);
      setArrowStyle(rawArrowStyle);
    },
    [windowWidth,
      windowHeight,
      isMobile,
      currentSiteSeeModalDims,
      siteSeeGuideId,
      styling],
  );

  // If the step has a function, run it.
  useEffect(() => {
    if (activeGuideData?.tourFunc && siteSeeGuideMode === 'tour') {
      activeGuideData.tourFunc();
    }
  }, [siteSeeGuideMode,
    activeGuideData,
    siteSeeGuideId]);

  return activeModalContent && (
    <Modal
      isOpen={showSiteSeeGuide}
      modalClassName={classNames('siteSeeGuide', {
        'modal--positioned': !isMobile && siteSeeGuideId,
        'modal--floating modal--dialog': isMobile || hasActiveNonTipContent,
        'modal--tip modal--dialog': !isMobile && siteSeeGuideId && Object.keys(modalStyle).length === 0,
      })}
      modalStyle={modalStyle}
      showBlur={activeModalContent.modalBlur}
      onExitFinish={onExitFinishFunc}
      handleModalHide={handleModalHide}
    >

      {/* We make an actual element becasue we can't position
         a pseudo element with JS */}
      {Object.keys(arrowStyle).length > 0 && (
        <div
          className="siteSeeGuide__modalArrow"
          style={arrowStyle}
        />
      )}

      <ModalContentBasic
        title={isMobile || !siteSeeGuideId ? activeModalContent.title : null}
        titleLength="long"
        text={activeModalContent.text}
        textLength="short"
        buttons={activeModalContent.buttons}
      >
        {activeModalContent.content}

        {activeModalContent.component && (
          <div className="modal__section modal__description modal__description--long">
            {isMobile && siteSeeGuideId && (
              <div className="siteSeeGuide__tipNumber">
                {tipCountPhrase}
              </div>
            )}
            <p>
              {activeModalContent.component}
            </p>
          </div>
        )}

        {!isMobile && siteSeeGuideId && (
        <div className="modal__section siteSeeGuide__titleWrapper">

          <div className={`siteSeeGuide__title siteSeeGuide__title--${siteSeeGuideMode}`}>
            {siteSeeGuideMode === 'tour' ? tipCountPhrase : 'Tip'}
            {' '}
            -
            {' '}
            {activeModalContent.title}
          </div>

          {siteSeeGuideMode === 'tour' && siteSeeGuideId !== 7 && (
          <button
            type="button"
            className="modal__btn siteSeeGuide__btn"
            onClick={
                    siteSeeGuideId === 7
                      ? () => {
                        setTrigger('endTour');
                        setShowSiteSeeGuide(false);
                      } : onNextTipClick
                  }
          >
            Next Tip
          </button>
          )}

        </div>
        )}
      </ModalContentBasic>

    </Modal>
  );
};

export default SiteSeeGuides;
